import type { AdminBateCarteiraLinha } from "@atlas/sdk";
import type { PillVariant } from "@atlas/ui/web";
import type { StatusNaFolha } from "@atlas/types";
import { STATUS_NA_FOLHA } from "@atlas/types";
import { PESO_STATUS_NA_FOLHA, ROTULO_STATUS_NA_FOLHA, statusNaFolha } from "@atlas/domain";

// Rotulos e classificacao do BATE DE CARTEIRA, os mesmos para o banco e para a
// averbadora.
//
// O status e' o STATUS NA FOLHA (#347), e o mapeamento mora em `@atlas/domain`
// (`statusNaFolha`), o mesmo que a API usa para a planilha. Antes cada tela
// tinha uma copia de `classify`, e a copia mandava para "Conciliado" todo
// status que nao reconhecia — inclusive `falha`, `parcial` e
// `suspensa_sem_folha`. Aqui nao ha fallback que concilie: so' `aplicada`.

export { PESO_STATUS_NA_FOLHA, ROTULO_STATUS_NA_FOLHA, STATUS_NA_FOLHA };

export const PILL_STATUS_NA_FOLHA: Record<StatusNaFolha, PillVariant> = {
  aplicada: "emdia",
  parcial: "expirado",
  nao_descontada: "rejeitada",
  suspensa: "pendente",
  aguardando_retorno: "pendente",
};

export const ROTULO_RESULTADO_FOLHA: Record<NonNullable<AdminBateCarteiraLinha["resultadoFolha"]>, string> = {
  recebida: "Aguardando retorno",
  aplicada: "Descontado",
  parcial: "Descontado em parte",
  falha: "Não descontado",
  interrompida_desligamento: "Interrompido (desligamento)",
  suspensa_sem_folha: "Suspenso (sem folha no mês)",
  suspensa_por_teto: "Suspenso (teto do órgão)",
};

export const ROTULO_ORIGEM: Record<AdminBateCarteiraLinha["origemContrato"], string> = {
  legado: "Legado",
  atlas: "Atlas",
};

/**
 * Status na folha da linha. `statusFolha` vem da API desde a #347; sem ele (a
 * web pode subir antes da API) sai do resultado cru, pelo MESMO mapeamento.
 */
export function statusFolhaDa(l: Pick<AdminBateCarteiraLinha, "statusFolha" | "resultadoFolha">): StatusNaFolha {
  return l.statusFolha ?? statusNaFolha(l.resultadoFolha);
}

export function resultadoFolhaDa(l: Pick<AdminBateCarteiraLinha, "resultadoFolha">): string {
  return l.resultadoFolha ? ROTULO_RESULTADO_FOLHA[l.resultadoFolha] : "Ainda não veio";
}

/** "3/24", "3" sem total conhecido, `null` sem cronograma. */
export function parcelaDa(l: Pick<AdminBateCarteiraLinha, "parcelaNumero" | "parcelasTotal">): string | null {
  if (!l.parcelaNumero) return null;
  return l.parcelasTotal ? `${l.parcelaNumero}/${l.parcelasTotal}` : String(l.parcelaNumero);
}

/** O que exige acao primeiro; dentro do status, pela ADF — a ordem nao danca. */
export function ordenarPorStatus<T extends AdminBateCarteiraLinha>(linhas: readonly T[]): T[] {
  return [...linhas].sort(
    (a, b) =>
      PESO_STATUS_NA_FOLHA[statusFolhaDa(a)] - PESO_STATUS_NA_FOLHA[statusFolhaDa(b)] ||
      String(a.adfBanco).localeCompare(String(b.adfBanco)),
  );
}

/**
 * Uma linha do CSV, com as MESMAS colunas da planilha do servidor
 * (`COLUNAS` em `apps/api/src/modules/admin/bate-carteira.ts`). O CPF vai
 * completo: relatorio vai sem censura, no escopo de quem o pede (diretriz de
 * 14/10/2026); a mascara e' so' da tela.
 */
export function linhaCsvBateCarteira(l: AdminBateCarteiraLinha): Record<string, string | number> {
  const reais = (v: number | null | undefined) => (v == null ? "" : v.toFixed(2));
  return {
    "Competência": l.competencia,
    "Consignatária": l.bancoNome,
    "Órgão/entidade": l.prefeituraNome,
    "Órgão/entidade (id)": l.unidadeId,
    ADF: l.adfBanco,
    "ID único (protocolo)": l.idUnico,
    "Matrícula": l.matricula,
    CPF: l.cpf || l.cpfMasked,
    Servidor: l.servidorNome,
    Origem: ROTULO_ORIGEM[l.origemContrato],
    "Situação do contrato": l.situacao,
    Parcela: parcelaDa(l) ?? "",
    "Valor instruído": reais(l.valorInstruido),
    "Status na folha": ROTULO_STATUS_NA_FOLHA[statusFolhaDa(l)],
    "Resultado na folha": resultadoFolhaDa(l),
    "Valor descontado": reais(l.valorDescontado),
    "Resíduo": reais(l.residuo),
    Motivo: l.motivo ?? "",
    "Saldo devedor": reais(l.saldoDevedor),
    "Parcelas restantes": l.parcelasRestantes ?? "",
    Cronograma: l.cronogramaPendente ? "Pendente" : "Informado",
  };
}
